import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User, Notification, AppState } from '@/types';

// Интерфейс для основного состояния приложения
interface AppStore extends AppState {
  // Действия для пользователя
  setUser: (user: User | null) => void;
  
  // Действия для темы
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleTheme: () => void;
  
  // Действия для сайдбара
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  
  // Действия для уведомлений
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  markNotificationAsRead: (id: string) => void;
  clearAllNotifications: () => void;
  
  // Утилитарные действия
  reset: () => void;
}

// Начальное состояние
const initialState: AppState = {
  user: null,
  theme: 'system',
  sidebarOpen: false,
  notifications: [],
};

// Создаем store
export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Пользователь
        setUser: (user) => set({ user }, false, 'setUser'),

        // Тема
        setTheme: (theme) => {
          set({ theme }, false, 'setTheme');
          
          // Применяем тему к документу
          const root = document.documentElement;
          
          if (theme === 'dark') {
            root.classList.add('dark');
          } else if (theme === 'light') {
            root.classList.remove('dark');
          } else {
            // System theme
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (isDark) {
              root.classList.add('dark');
            } else {
              root.classList.remove('dark');
            }
          }
        },

        toggleTheme: () => {
          const { theme } = get();
          const newTheme = theme === 'light' ? 'dark' : 'light';
          get().setTheme(newTheme);
        },

        // Сайдбар
        setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }, false, 'setSidebarOpen'),
        
        toggleSidebar: () => {
          const { sidebarOpen } = get();
          set({ sidebarOpen: !sidebarOpen }, false, 'toggleSidebar');
        },

        // Уведомления
        addNotification: (notification) => {
          const id = Math.random().toString(36).substr(2, 9);
          const timestamp = new Date().toISOString();
          const newNotification: Notification = {
            ...notification,
            id,
            timestamp,
            read: false,
          };
          
          set(
            (state) => ({
              notifications: [newNotification, ...state.notifications].slice(0, 50), // Лимит 50 уведомлений
            }),
            false,
            'addNotification'
          );
        },

        removeNotification: (id) => {
          set(
            (state) => ({
              notifications: state.notifications.filter((notification) => notification.id !== id),
            }),
            false,
            'removeNotification'
          );
        },

        markNotificationAsRead: (id) => {
          set(
            (state) => ({
              notifications: state.notifications.map((notification) =>
                notification.id === id ? { ...notification, read: true } : notification
              ),
            }),
            false,
            'markNotificationAsRead'
          );
        },

        clearAllNotifications: () => {
          set({ notifications: [] }, false, 'clearAllNotifications');
        },

        // Сброс состояния
        reset: () => set(initialState, false, 'reset'),
      }),
      {
        name: 'wormtracker-app-state',
        partialize: (state) => ({
          theme: state.theme,
          user: state.user,
          // Не сохраняем в localStorage: sidebarOpen, notifications
        }),
      }
    ),
    {
      name: 'WormTracker App Store',
    }
  )
);

// Хуки для удобного использования
export const useUser = () => useAppStore((state) => state.user);
export const useTheme = () => useAppStore((state) => state.theme);
export const useSidebarOpen = () => useAppStore((state) => state.sidebarOpen);
export const useNotifications = () => useAppStore((state) => state.notifications);

// Селекторы
export const useUnreadNotificationsCount = () =>
  useAppStore((state) => state.notifications.filter((n) => !n.read).length);

export const useRecentNotifications = (limit: number = 5) =>
  useAppStore((state) => state.notifications.slice(0, limit));

// Store для данных фермы
interface FarmDataStore {
  // Кэшированные данные
  stacks: any[];
  feedTypes: any[];
  recentFeedingRecords: any[];
  dashboardStats: any | null;
  
  // Состояние загрузки
  loading: {
    stacks: boolean;
    feedTypes: boolean;
    feedingRecords: boolean;
    dashboardStats: boolean;
  };
  
  // Ошибки
  errors: {
    stacks: string | null;
    feedTypes: string | null;
    feedingRecords: string | null;
    dashboardStats: string | null;
  };
  
  // Последнее обновление
  lastUpdated: {
    stacks: string | null;
    feedTypes: string | null;
    feedingRecords: string | null;
    dashboardStats: string | null;
  };
  
  // Действия
  setStacks: (stacks: any[]) => void;
  setFeedTypes: (feedTypes: any[]) => void;
  setRecentFeedingRecords: (records: any[]) => void;
  setDashboardStats: (stats: any) => void;
  
  setLoading: (key: keyof FarmDataStore['loading'], loading: boolean) => void;
  setError: (key: keyof FarmDataStore['errors'], error: string | null) => void;
  
  // Обновление timestamp
  updateTimestamp: (key: keyof FarmDataStore['lastUpdated']) => void;
  
  // Проверка актуальности данных
  isDataStale: (key: keyof FarmDataStore['lastUpdated'], maxAgeMinutes?: number) => boolean;
  
  // Очистка
  clearData: () => void;
}

const initialFarmState = {
  stacks: [],
  feedTypes: [],
  recentFeedingRecords: [],
  dashboardStats: null,
  loading: {
    stacks: false,
    feedTypes: false,
    feedingRecords: false,
    dashboardStats: false,
  },
  errors: {
    stacks: null,
    feedTypes: null,
    feedingRecords: null,
    dashboardStats: null,
  },
  lastUpdated: {
    stacks: null,
    feedTypes: null,
    feedingRecords: null,
    dashboardStats: null,
  },
};

export const useFarmDataStore = create<FarmDataStore>()(
  devtools(
    (set, get) => ({
      ...initialFarmState,
      
      setStacks: (stacks) => {
        set({ stacks }, false, 'setStacks');
        get().updateTimestamp('stacks');
      },
      
      setFeedTypes: (feedTypes) => {
        set({ feedTypes }, false, 'setFeedTypes');
        get().updateTimestamp('feedTypes');
      },
      
      setRecentFeedingRecords: (recentFeedingRecords) => {
        set({ recentFeedingRecords }, false, 'setRecentFeedingRecords');
        get().updateTimestamp('feedingRecords');
      },
      
      setDashboardStats: (dashboardStats) => {
        set({ dashboardStats }, false, 'setDashboardStats');
        get().updateTimestamp('dashboardStats');
      },
      
      setLoading: (key, loading) => {
        set(
          (state) => ({
            loading: { ...state.loading, [key]: loading },
          }),
          false,
          `setLoading-${key}`
        );
      },
      
      setError: (key, error) => {
        set(
          (state) => ({
            errors: { ...state.errors, [key]: error },
          }),
          false,
          `setError-${key}`
        );
      },
      
      updateTimestamp: (key) => {
        const now = new Date().toISOString();
        set(
          (state) => ({
            lastUpdated: { ...state.lastUpdated, [key]: now },
          }),
          false,
          `updateTimestamp-${key}`
        );
      },
      
      isDataStale: (key, maxAgeMinutes = 5) => {
        const { lastUpdated } = get();
        const timestamp = lastUpdated[key];
        
        if (!timestamp) return true;
        
        const lastUpdateTime = new Date(timestamp).getTime();
        const now = new Date().getTime();
        const maxAge = maxAgeMinutes * 60 * 1000; // в миллисекундах
        
        return now - lastUpdateTime > maxAge;
      },
      
      clearData: () => set(initialFarmState, false, 'clearData'),
    }),
    {
      name: 'WormTracker Farm Data Store',
    }
  )
);

// Store для системных настроек
interface SystemSettingsStore {
  // Данные настроек
  settings: any | null;
  timezones: any[];
  languages: any[];
  themes: any[];
  substrates: any[];
  
  // Состояние загрузки
  loading: boolean;
  saving: boolean;
  
  // Ошибки
  error: string | null;
  
  // Действия
  setSettings: (settings: any) => void;
  setTimezones: (timezones: any[]) => void;
  setLanguages: (languages: any[]) => void;
  setThemes: (themes: any[]) => void;
  setSubstrates: (substrates: any[]) => void;
  
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setError: (error: string | null) => void;
  
  // Обновление отдельных настроек
  updateFarmInfo: (farmName: string, farmArea: number, rackCount: number) => void;
  updateTimezone: (timezone: string) => void;
  updateTheme: (theme: string) => void;
  updateNotificationSettings: (settings: any) => void;
  updateVoiceSettings: (settings: any) => void;
  updateBackupSettings: (settings: any) => void;
  
  clearSettings: () => void;
}

export const useSystemSettingsStore = create<SystemSettingsStore>()(
  devtools(
    (set, get) => ({
      settings: null,
      timezones: [],
      languages: [],
      themes: [],
      substrates: [],
      loading: false,
      saving: false,
      error: null,
      
      setSettings: (settings) => set({ settings }, false, 'setSettings'),
      setTimezones: (timezones) => set({ timezones }, false, 'setTimezones'),
      setLanguages: (languages) => set({ languages }, false, 'setLanguages'),
      setThemes: (themes) => set({ themes }, false, 'setThemes'),
      setSubstrates: (substrates) => set({ substrates }, false, 'setSubstrates'),
      
      setLoading: (loading) => set({ loading }, false, 'setLoading'),
      setSaving: (saving) => set({ saving }, false, 'setSaving'),
      setError: (error) => set({ error }, false, 'setError'),
      
      updateFarmInfo: (farmName, farmArea, rackCount) => {
        const { settings } = get();
        if (settings) {
          const updatedSettings = {
            ...settings,
            farm_name: farmName,
            farm_area: farmArea,
            rack_count: rackCount,
          };
          set({ settings: updatedSettings }, false, 'updateFarmInfo');
        }
      },
      
      updateTimezone: (timezone) => {
        const { settings } = get();
        if (settings) {
          const updatedSettings = { ...settings, timezone };
          set({ settings: updatedSettings }, false, 'updateTimezone');
        }
      },
      
      updateTheme: (theme) => {
        const { settings } = get();
        if (settings) {
          const updatedSettings = { ...settings, theme };
          set({ settings: updatedSettings }, false, 'updateTheme');
        }
      },
      
      updateNotificationSettings: (notificationSettings) => {
        const { settings } = get();
        if (settings) {
          const updatedSettings = { ...settings, ...notificationSettings };
          set({ settings: updatedSettings }, false, 'updateNotificationSettings');
        }
      },
      
      updateVoiceSettings: (voiceSettings) => {
        const { settings } = get();
        if (settings) {
          const updatedSettings = { ...settings, ...voiceSettings };
          set({ settings: updatedSettings }, false, 'updateVoiceSettings');
        }
      },
      
      updateBackupSettings: (backupSettings) => {
        const { settings } = get();
        if (settings) {
          const updatedSettings = { ...settings, ...backupSettings };
          set({ settings: updatedSettings }, false, 'updateBackupSettings');
        }
      },
      
      clearSettings: () => set({
        settings: null,
        timezones: [],
        languages: [],
        themes: [],
        substrates: [],
        loading: false,
        saving: false,
        error: null,
      }, false, 'clearSettings'),
    }),
    {
      name: 'WormTracker System Settings Store',
    }
  )
);

// Инициализация темы при загрузке приложения
export const initializeTheme = () => {
  const { theme, setTheme } = useAppStore.getState();
  setTheme(theme); // Это применит тему к DOM
};

// Подписка на изменения системной темы
export const subscribeToSystemTheme = () => {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  
  const handleChange = () => {
    const { theme, setTheme } = useAppStore.getState();
    if (theme === 'system') {
      setTheme('system'); // Это обновит DOM
    }
  };
  
  mediaQuery.addEventListener('change', handleChange);
  
  // Возвращаем функцию для отписки
  return () => mediaQuery.removeEventListener('change', handleChange);
};
